import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { HOME } from '@/constants/testIds';

// Chief Shirt Officer facts — small credibility grid to the right of the hero.
const TEEODORE_STATS = [
  { label: 'Designs approved', value: '100%', hint: 'Every shirt gets the thumbs up before it ships.' },
  { label: 'Bad ideas rejected', value: '0', hint: 'There are no bad shirt ideas around here.' },
  { label: 'Coffee refills / day', value: '∞', hint: 'Fuel for the print press.' },
  { label: 'Hats owned', value: '1', hint: "It's the only one he needs." },
];

const TeeodoreSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-15%' });

  useEffect(() => {
    // Preload the hero image so the reveal animation isn't waiting on network.
    const img = new Image();
    img.src = '/brand/teeodore.png';
  }, []);

  return (
    <section
      ref={ref}
      id="teeodore"
      data-testid={HOME.teeodoreSection}
      className="relative py-24 md:py-32 px-6 bg-black overflow-hidden"
    >
      {/* Orange glow accent */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[36rem] h-[36rem] rounded-full opacity-25 blur-3xl"
        style={{ background: 'radial-gradient(circle, #FF6600 0%, transparent 60%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-24 w-[30rem] h-[30rem] rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(circle, #FF6600 0%, transparent 60%)' }}
      />
      {/* Grain overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        }}
      />

      <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
        {/* Mascot column */}
        <motion.div
          initial={{ opacity: 0, x: -60, rotate: -3 }}
          animate={inView ? { opacity: 1, x: 0, rotate: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5 relative flex justify-center md:justify-start"
        >
          <motion.img
            src="/brand/teeodore.png"
            alt="Teeodore, Chief Shirt Officer of Shirt Happens Studio"
            data-testid={HOME.teeodoreImage}
            className="w-full max-w-[380px] md:max-w-[440px] drop-shadow-[0_20px_45px_rgba(255,102,0,0.35)] select-none"
            draggable={false}
            animate={inView ? { y: [0, -12, 0] } : {}}
            transition={{ y: { duration: 4, repeat: Infinity, ease: 'easeInOut' } }}
          />
        </motion.div>

        {/* Text column */}
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-brand-orange/15 border border-brand-orange/40 mb-6">
              <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
              <span className="font-display font-black text-[11px] tracking-[0.32em] uppercase text-brand-orange">
                Meet the team
              </span>
            </div>
            <h2 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-white mb-3">
              Teeodore
            </h2>
            <div className="font-display font-black text-brand-orange text-lg sm:text-xl tracking-[0.24em] uppercase mb-6">
              Chief Shirt Officer
            </div>
            <p className="font-body text-base sm:text-lg text-white/75 leading-relaxed mb-8 max-w-xl">
              Every design that leaves this studio gets the Teeodore stamp of approval &mdash; a very
              official <span className="text-white font-semibold">thumbs-up</span>. He runs quality
              control, hangs every finished tee, and has strong opinions about hat placement.
            </p>
            <p className="font-body text-base sm:text-lg text-white/75 leading-relaxed mb-10 max-w-xl italic">
              &ldquo;If Teeodore didn&rsquo;t give it a thumbs-up, you don&rsquo;t get it.&rdquo;
            </p>
          </motion.div>

          {/* Fun stats grid */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-2 gap-3 sm:gap-4"
          >
            {TEEODORE_STATS.map((stat, i) => (
              <div
                key={stat.label}
                data-testid={`${HOME.teeodoreStat}-${i}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-4 sm:p-5 hover:border-brand-orange/50 hover:bg-brand-orange/[0.06] transition-colors"
              >
                <div className="font-display font-black text-3xl sm:text-4xl text-brand-orange leading-none">
                  {stat.value}
                </div>
                <div className="mt-2 font-display font-black text-[10px] tracking-[0.24em] uppercase text-white/85">
                  {stat.label}
                </div>
                <div className="mt-1 font-body text-xs text-white/45 leading-snug">
                  {stat.hint}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TeeodoreSection;
